import { CognitoUser } from "amazon-cognito-identity-js";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContainer } from "../../components/auth/container";
import { LoginForm } from "../../components/auth/login.form";
import { CognitoContext, UserProviderActions } from "../../contexts/cognito/cognito-provider";
import { CognitoSignin } from "../../library/cognito/signin";
import { AppRouteNames } from "../../routes/app-routes";
import { CurrentUserStorage } from "../../library/core/current-user";
import { buildUserSession } from "../../library/cognito/session";
import { ResetPasswordForm } from "../../components/auth/reset-password";
import { ToggleView } from "../../components/views/toggle-view";
import { CognitoGetErrorMessage } from "../../library/cognito/confirm";
import { ForgotPasswordForm } from "../../components/auth/forgot-password";
import { ForceResetPasswordForm } from "../../components/auth/force-reset-password";
import { VerifyCodeForm } from "../../components/auth/verify-account.form";
import { Alert, Alerts } from "../../components/bootstrap/alert";
import { useAppLoading } from "../../components/layout/app-loading-provider";
import { useToast } from "../../components/toast/toast-context";
const Modes = {
  normal: 'normal',
  resetPwd: 'resetPwd',
  userNotConfirmed: 'userNotConfirmed',
  userRemoved: 'userRemoved',
  forgotPwd: 'forgotPwd',
  forceChangePwd: 'forceChangePwd'
};

const LoginPage = () => {
  const { showLoading, hideLoading} = useAppLoading();
  const { success, error, info, warning, show } = useToast();

  const [errorMessage, setErrorMessage] = useState(null);
  const [pageState, setPageState] = useState({
    mode: Modes.normal
  });

  const { state, dispatch } = useContext(CognitoContext);
  const [queryStr] = useSearchParams();
  const navigate = useNavigate();

  const targetUrl = queryStr.get('url') || AppRouteNames.redeemVoucher;
  const username = queryStr.get('username');

  const onErrorHandler = (err) => {
    hideLoading(); 
    try {
      console.log('onError', err);
      console.log(`err.code=${err.code}`);
      console.log(`err.name=${err.name}`);
      console.log(`err.message=${err.message}`);

      const errMessage = CognitoGetErrorMessage(err);
      console.log('CognitoGetErrorMessage', errMessage);
      setErrorMessage(errMessage);
     // error(errMessage);
    } catch {
      console.log(JSON.stringify(err));
      setErrorMessage('Incorrect username or password.');
    }
  };

  const onForceChangePasswordHandler = ({
    username,
    completeNewPasswordHandler
  }) => {
    hideLoading(); 
    setPageState((ps) => ({
      ...ps,
      mode: Modes.forceChangePwd,
      completeNewPasswordHandler,
      username
    }));
  };

  const navigateToLogin = () => {
    dispatch({
      type: UserProviderActions.setSession,
      session: null
    });
    navigate(AppRouteNames.signin);
  };

  const signInHandler = (model) => {
    setErrorMessage(null);
    CurrentUserStorage.saveCurrentUser(model.username);

    showLoading();

    CognitoSignin({
      cognitoContext: state,
      username: model.username,
      password: model.password,
      keepMeLoggedIn: model.keepMeLoggedIn,
      onForceChangePassword: onForceChangePasswordHandler,
      onSuccess: (data) => {
        const userSession = buildUserSession(data.cognitoSession, data.attributes);
        dispatch({ type: UserProviderActions.setSession, session: userSession });
        dispatch({ type: UserProviderActions.setCognitoUser, cognitoUser: data.cognitoUser });

        navigate(targetUrl);
      },
      onError: onErrorHandler,
      onResetPassword: () => {
        hideLoading();
        setPageState((ps) => ({
          ...ps,
          mode: Modes.resetPwd
        }));
      },
      onUserNotConfirmed: () => {
        hideLoading();
        setPageState((ps) => ({
          ...ps,
          mode: Modes.userNotConfirmed,
          username: model.username,
          password: model.password
        }));
      },
      onUserRemoved: () => {
        hideLoading();
        navigateToLogin();
      }
    });
  };


  const onResetPasswordSuccess = ({ username, password }) => {
    signInHandler({ username, password, keepMeLoggedIn: true });
  };

  const onForgotPasswordHandler = () => {
    hideLoading();
    setPageState((ps) => ({
      ...ps,
      mode: Modes.forgotPwd
    }));
  };

  const onProcceedToResetPassword = ({ username }) => {
    CurrentUserStorage.saveCurrentUser(username);
    hideLoading();
    setPageState((ps) => ({
      ...ps,
      mode: Modes.resetPwd
    }));
  };

  const onCancelResetPassword = () => {
    setErrorMessage(null);
    hideLoading();
    setPageState((ps) => ({
      ...ps,
      mode: Modes.normal
    }));
  };

  const onVerificationCompleted = () => {
    showLoading();
    signInHandler({
      username: pageState.username,
      password: pageState.password,
      keepMeLoggedIn: true
    });
  };

  return (
    <AuthContainer>
      <ToggleView show={pageState.mode === Modes.normal}>
        <LoginForm
          keepMeLoggedIn
          username={username}
          signinHandler={signInHandler}
          onForgotPassword={onForgotPasswordHandler}
          errorMessage={errorMessage}
        />
      </ToggleView>

      <ToggleView show={pageState.mode === Modes.resetPwd}>
        <ResetPasswordForm
          onSuccess={onResetPasswordSuccess}
          onError={onErrorHandler}
          errorMessage={errorMessage}
          onCancel={onCancelResetPassword}
        />
      </ToggleView>

      <ToggleView show={pageState.mode === Modes.forgotPwd}>
        <ForgotPasswordForm
          onProcceed={onProcceedToResetPassword}
          errorMessage={errorMessage}
          onCancel={onCancelResetPassword}
        />
      </ToggleView>

      <ToggleView show={pageState.mode === Modes.forceChangePwd}>
        <ForceResetPasswordForm
          username={pageState.username}
          completeNewPasswordHandler={pageState.completeNewPasswordHandler}
          onSuccess={onResetPasswordSuccess}
          onError={onErrorHandler}
          errorMessage={errorMessage}
          onCancel={onCancelResetPassword}
        />
      </ToggleView>

      <ToggleView show={pageState.mode === Modes.userNotConfirmed}>
        <Alert type={Alerts.warning}>
          <p>
            Your account still needs verification. Please enter the code to complete your
            account setup. We've sent a verification code to your email address. Please
            enter the code to complete your account setup.
          </p>
        </Alert>

        <VerifyCodeForm
          username={pageState.username}
          onSuccess={onVerificationCompleted}
          onError={onErrorHandler}
        />
      </ToggleView>
    </AuthContainer>
  );
};

export { LoginPage };
